import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Award, Activity, Calendar, X, CheckCircle, Building, FileText } from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';

const Analytics = () => {
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalOrganizations: 0,
    totalAssessments: 0,
    certifiedOrganizations: 0,
    activeUsers: 0,
    growthRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ title: '', content: [], type: '' });
  const [detailedData, setDetailedData] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/analytics`);
      const data = await response.json();
      
      setAnalytics({
        totalUsers: data.total_users,
        totalOrganizations: data.total_organizations,
        totalAssessments: data.total_assessments,
        certifiedOrganizations: data.certified_organizations,
        activeUsers: data.active_users,
        growthRate: data.growth_rate
      });
      
      setDetailedData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics({
        totalUsers: 0,
        totalOrganizations: 0,
        totalAssessments: 0,
        certifiedOrganizations: 0,
        activeUsers: 0,
        growthRate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (type) => {
    try {
      let title = '';
      let content = [];
      
      switch(type) {
        case 'users':
          title = `All Users (${analytics.totalUsers})`;
          const usersRes = await fetch(`${BACKEND_URL}/api/admin/users`);
          const usersData = await usersRes.json();
          content = usersData.map(u => ({ name: u.username, detail: u.role, status: u.status || 'active' }));
          break;
          
        case 'organizations':
          title = `All Organizations (${analytics.totalOrganizations})`;
          const orgsRes = await fetch(`${BACKEND_URL}/api/admin/organizations`);
          const orgsData = await orgsRes.json();
          content = orgsData.map(o => ({ 
            name: o.name, 
            detail: o.certification_level || 'Not Certified', 
            status: o.status 
          }));
          break;
          
        case 'certified':
          title = `Certified Organizations (${analytics.certifiedOrganizations})`;
          const certOrgsRes = await fetch(`${BACKEND_URL}/api/admin/organizations`);
          const certOrgsData = await certOrgsRes.json();
          content = certOrgsData
            .filter(o => o.certification_level)
            .map(o => ({ 
              name: o.name, 
              detail: `${o.certification_level} - Score: ${o.certification_score}`, 
              status: 'certified' 
            }));
          break;
          
        case 'assessments':
          title = `All Assessments (${analytics.totalAssessments})`;
          const assessRes = await fetch(`${BACKEND_URL}/api/admin/assessment-monitoring`);
          const assessData = await assessRes.json();
          content = assessData.map(a => ({ 
            name: a.organization_name || `Assessment ${a.assessment_id?.substring(0, 8)}`, 
            detail: `Phase: ${a.current_phase}`, 
            status: a.overall_status 
          }));
          break;
          
        case 'active':
          title = `Active Users (${analytics.activeUsers})`;
          content = [{ 
            name: 'Active Users', 
            detail: `Estimated as users with recent activity (${analytics.activeUsers} users)`, 
            status: 'info' 
          }];
          break;
          
        case 'growth':
          title = `Monthly Growth Rate: ${analytics.growthRate}%`;
          content = [
            { name: 'Growth Calculation', detail: 'Based on new users in last 30 days vs previous 30 days', status: 'info' },
            { name: 'Current Rate', detail: `${analytics.growthRate}% monthly growth`, status: analytics.growthRate > 0 ? 'positive' : 'neutral' }
          ];
          break;
      }
      
      setModalData({ title, content, type });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching modal data:', error);
    }
  };

  const cards = [
    {
      id: 'users',
      title: 'Total Users',
      value: analytics.totalUsers,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      trend: '+12%'
    },
    {
      id: 'organizations',
      title: 'Total Organizations',
      value: analytics.totalOrganizations,
      icon: Building,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      trend: '+8%'
    },
    {
      id: 'certified',
      title: 'Certified Organizations',
      value: analytics.certifiedOrganizations,
      icon: Award,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      trend: '+15%'
    },
    {
      id: 'assessments',
      title: 'Total Assessments',
      value: analytics.totalAssessments,
      icon: FileText,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      trend: '+20%'
    },
    {
      id: 'active',
      title: 'Active Users (30 days)',
      value: analytics.activeUsers,
      icon: Activity,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      trend: '+5%'
    },
    {
      id: 'growth',
      title: 'Monthly Growth Rate',
      value: `${analytics.growthRate}%`,
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      trend: analytics.growthRate > 0 ? 'Positive' : 'Stable'
    }
  ];

  if (loading) {
    return (
      <SystemAdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </SystemAdminLayout>
    );
  }

  return (
    <SystemAdminLayout>
      <div className="bg-gray-50 min-h-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-8 px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center space-x-3">
              <BarChart3 className="h-10 w-10" />
              <div>
                <h1 className="text-4xl font-black mb-2">Platform Analytics</h1>
                <p className="text-lg text-blue-100">Real-time platform performance and user engagement metrics</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {cards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  onClick={() => handleCardClick(card.id)}
                  className={`${card.bgColor} rounded-xl shadow-lg p-6 cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-2xl border-2 border-transparent hover:border-${card.textColor.split('-')[1]}-300`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <span className={`text-sm font-semibold ${card.textColor} flex items-center`}>
                      <TrendingUp className="w-4 h-4 mr-1" />
                      {card.trend}
                    </span>
                  </div>
                  <p className="text-4xl font-black text-gray-900 mb-1">{card.value}</p>
                  <p className="text-gray-600 text-sm font-semibold">{card.title}</p>
                  <p className="text-xs text-gray-500 mt-2">Click to view details</p>
                </div>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* User Growth Trend Chart */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">User Growth Trend</h3>
              {detailedData && detailedData.user_growth_trend ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={detailedData.user_growth_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={3} name="New Users" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </div>

            {/* Assessment Completion Chart */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Assessment Completion Rate</h3>
              {detailedData && detailedData.assessment_completion_trend ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={detailedData.assessment_completion_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#8B5CF6" name="Completed Assessments" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold">{modalData.title}</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {modalData.content.length > 0 ? (
                  <div className="space-y-3">
                    {modalData.content.map((item, index) => (
                      <div
                        key={index}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-600 mt-1">{item.detail}</p>
                          </div>
                          <div>
                            {item.status === 'active' || item.status === 'approved' || item.status === 'certified' || item.status === 'positive' ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : item.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-blue-500" />
                            ) : (
                              <Activity className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-200">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SystemAdminLayout>
  );
};

export default Analytics;
